import { AlertTriangleIcon } from "../../icons.js";
import type { WidgetProps } from "../types.js";

type AllergyPayload = {
  substance?: string;
  category?: string;
  severity?: string;
  reaction?: string;
};

const SEVERITY_COLOR: Record<string, string> = {
  life_threatening: "text-red-600 dark:text-red-400",
  severe: "text-red-500 dark:text-red-400",
  moderate: "text-amber-600 dark:text-amber-400",
  mild: "text-yellow-600 dark:text-yellow-400",
};

export function AllergiesWidget({ events, onNavigate }: WidgetProps) {
  const allergies = events.filter((e) => e.eventType === "allergy");
  const medAllergies = allergies.filter((e) => (e.payload as AllergyPayload).category === "medication");
  const top = allergies.slice(0, 4);

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <AlertTriangleIcon size={16} className="text-(--accent)" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Allergies</h2>
        <button onClick={() => onNavigate("allergies")} className="ml-auto text-xs text-(--accent) hover:underline">
          View all
        </button>
      </div>
      {allergies.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">No allergies recorded</p>
      ) : (
        <>
          <div className="mb-2 flex gap-3 text-sm text-gray-700 dark:text-gray-300">
            <span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{allergies.length}</span> total
            </span>
            {medAllergies.length > 0 && (
              <span>
                <span className="font-medium text-red-600 dark:text-red-400">{medAllergies.length}</span> medication
              </span>
            )}
          </div>
          <ul className="space-y-0.5">
            {top.map((e) => {
              const p = e.payload as AllergyPayload;
              return (
                <li key={e.eventId} className="flex items-center justify-between text-xs">
                  <span className="truncate text-gray-700 dark:text-gray-300">{p.substance ?? "Unknown"}</span>
                  {p.severity && (
                    <span className={`ml-2 shrink-0 ${SEVERITY_COLOR[p.severity] ?? "text-gray-500"}`}>
                      {p.severity.replace(/_/g, " ")}
                    </span>
                  )}
                </li>
              );
            })}
            {allergies.length > 4 && (
              <li className="text-xs text-gray-400 dark:text-gray-500">+{allergies.length - 4} more</li>
            )}
          </ul>
        </>
      )}
    </section>
  );
}
